import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './styles';
import { selectCulture } from '../../store/selectors';
import useTexts from '../../hooks/useTexts';
import { setCulture } from '../../store/main/slice';
import { useSession } from '../../contexts/SessionContext';
import { logout } from '../../api/magicLinkApi';
import SessionCountdown from '../SessionCountdown';

const CULTURES = ['cs-CZ', 'en-US'];

export const Header = () => {
  const dispatch = useDispatch();
  const texts = useTexts();
  const { classes } = useStyles();
  const culture = useSelector(selectCulture);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { isAuthenticated, setAuthenticated } = useSession();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const onCultureChange = (value: string) => {
    dispatch(setCulture(value));
  };

  const handleLogout = useCallback(async () => {
    await logout();
    setAuthenticated(false);
    localStorage.removeItem('last_selected_api_key');
    navigate('/', { replace: true });
  }, [setAuthenticated, navigate]);

  const handleAutoLogout = useCallback(() => {
    setAuthenticated(false);
    localStorage.removeItem('last_selected_api_key');
    enqueueSnackbar(texts.SESSION_EXPIRED, {
      variant: 'warning',
      autoHideDuration: 6000,
    });
    navigate('/', { replace: true });
  }, [setAuthenticated, enqueueSnackbar, texts, navigate]);

  const menuContent = useMemo(
    () => (
      <>
        <section className="first">
          <ul>
            <li role="presentation">
              <a href="https://www.ppl.cz/osoby" role="menuitem">
                {texts.MENU_LINK1}
              </a>
            </li>
            <li role="presentation">
              <a href="https://www.ppl.cz/firmy" role="menuitem">
                {texts.MENU_LINK2}
              </a>
            </li>
          </ul>
        </section>
        <section className="seccond">
          <ul>
            <li role="presentation">
              <a href="https://www.ppl.cz/o-nas" role="menuitem">
                {texts.MENU_LINK3}
              </a>
            </li>
            <li role="presentation">
              <a href="https://www.ppl.cz/vyhledat-zasilku" role="menuitem">
                {texts.MENU_LINK4}
              </a>
            </li>
            <li role="presentation">
              <a href="https://www.ppl.cz/poslat-zasilku" role="menuitem">
                {texts.MENU_LINK5}
              </a>
            </li>
            <li role="presentation">
              <a href="https://ppl.jobs.cz" role="menuitem">
                {texts.MENU_LINK6}
              </a>
            </li>
            <li role="presentation">
              <a href="https://www.ppl.cz/zakaznicky-servis" role="menuitem">
                {texts.MENU_LINK7}
              </a>
            </li>
            {isAuthenticated && (
              <>
                <li role="presentation">
                  <SessionCountdown onExpire={handleAutoLogout} />
                </li>
                <li role="presentation">
                  <Button
                    variant="text"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    className={classes.logoutBtn}
                  >
                    {texts.LOGOUT}
                  </Button>
                </li>
              </>
            )}
            <li role="presentation" className="separ">
              <FormControl>
                <Select
                  variant="standard"
                  value={culture}
                  size="medium"
                  className={classes.languageSelectButton}
                  onChange={(e) => onCultureChange(e.target.value as string)}
                  IconComponent={ArrowDropDownIcon}
                  MenuProps={{ sx: { zIndex: 1400 } }}
                  sx={{
                    '& .MuiSelect-icon': {
                      display: 'inline-block',
                      color: 'inherit',
                      width: '1em',
                      height: '1em',
                    },
                  }}
                >
                  {CULTURES.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x.substring(0, 2).toLocaleUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </li>
          </ul>
        </section>
      </>
    ),
    [
      texts,
      culture,
      classes.languageSelectButton,
      classes.logoutBtn,
      isAuthenticated,
      handleAutoLogout,
      handleLogout,
    ]
  );

  return (
    <div className={classes.header}>
      <Box className={classes.header__logo}>
        <a href="https://www.ppl.cz">
          <img alt="Logo PPL" width="100%" height="100%" src="/img/logo.svg" />
        </a>
      </Box>

      <Button
        variant="text"
        className={classes.menuBtn}
        onClick={() => setMenuIsOpen(!menuIsOpen)}
      >
        {texts.MENU} <MenuIcon />
      </Button>

      <Box className={classes.navbar}>{menuContent}</Box>

      <Drawer
        open={menuIsOpen}
        onClose={() => setMenuIsOpen(false)}
        classes={{ paper: classes.drawer }}
        anchor="top"
        transitionDuration={0}
      >
        {menuContent}
      </Drawer>
    </div>
  );
};

export default Header;
